/**
 * YaariplayWidget — public "today's game" surface.
 *
 * Uses `useYaariplayStats()` (PUBLIC endpoint) so the widget renders for both
 * authenticated and anonymous visitors. Called from the marketing homepage
 * hero and the dashboard right-rail (see RightRailWidgets.tsx).
 *
 * Design goals:
 *  - Wordle-style tile preview at the top gives the game an instant visual
 *    identity (users recognise the mechanic without reading a word).
 *  - Copy shifts by player count: "be the first" (0) / "join N" (1+) — feels
 *    warm rather than statistical.
 *  - Countdown to the next puzzle reinforces the "daily" habit.
 *  - Top-solver call-out drives competitive curiosity.
 *
 * Never renders the answer or any auth-only detail.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gamepad2, Trophy } from 'lucide-react';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  UserAvatar,
  useToast,
} from '@/components/ui';
import {
  useYaariplayFriendsToday,
  useYaariplayNudge,
  useYaariplayStats,
} from '@/lib/api';
import { useAuth } from '@/pages/auth';

/** Hours remaining until midnight IST (the daily puzzle reset). */
function hoursUntilResetIST(): number {
  const now = new Date();
  // Midnight IST = 18:30 UTC (previous day). Compute the next reset instant.
  const utcHour = now.getUTCHours();
  const utcMin = now.getUTCMinutes();
  const minutesNow = utcHour * 60 + utcMin;
  const minutesToReset = (18 * 60 + 30 - minutesNow + 24 * 60) % (24 * 60);
  return Math.max(1, Math.ceil(minutesToReset / 60));
}

/** 5-tile teaser row with brand-colored greens + soft yellows. Static — this
 *  is a preview / decoration, not a live board. */
function TilePreview() {
  const tiles: Array<'g' | 'y' | 'e'> = ['g', 'e', 'y', 'e', 'e'];
  return (
    <div className="mb-3 flex gap-1" aria-hidden="true">
      {tiles.map((t, i) => (
        <div
          key={i}
          className={
            'h-7 w-7 rounded-sm ' +
            (t === 'g'
              ? 'bg-brand-600'
              : t === 'y'
                ? 'bg-amber-400'
                : 'border border-border bg-surface-2')
          }
        />
      ))}
    </div>
  );
}

/** Inline friends-played section used ONLY in the right-rail widget. Shows
 *  up to 5 friend names with a solved/not-solved indicator, plus a Nudge
 *  button for the first few who haven't played. */
function WidgetFriendsLine() {
  const token = useAuth((s) => s.token);
  const q = useYaariplayFriendsToday();
  const nudge = useYaariplayNudge();
  const { toast } = useToast();
  const [sent, setSent] = useState<Set<number>>(new Set());

  if (!token) return null;
  const allFriends = q.data?.friends ?? [];
  if (q.isLoading || allFriends.length === 0) return null;

  // Hard cap at 5 total — client-side safety even when a stale server
  // returns more. Server also caps and tops up with random newcomers
  // registered in the last 7 days when the friend set is small.
  const friends = allFriends.slice(0, 5);
  const played = friends.filter((f) => f.played);
  const notPlayed = friends.filter((f) => !f.played);

  const onNudge = async (userId: number, username: string) => {
    if (sent.has(userId)) return;
    try {
      await nudge.mutateAsync(userId);
      setSent((prev) => new Set(prev).add(userId));
      toast({ title: `Invited @${username}` });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not invite';
      toast({ title: msg, variant: 'destructive' });
    }
  };

  // One row = avatar + (name over @handle) + right-side state. Shared by
  // played and not-played sections so the widget reads as a single list.
  // `right` is either the solved/tried glyph or the Remind button.
  const renderRow = (
    f: typeof friends[number],
    right: React.ReactNode,
  ) => {
    const display = f.firstname?.trim() || f.username;
    const initial = (display || '?').slice(0, 1).toUpperCase();
    return (
      <li key={f.user_id} className="flex items-center gap-2">
        <Link to={`/user/${f.username}`} className="shrink-0" aria-label={`Open @${f.username}'s profile`}>
          <UserAvatar
            src={f.image || undefined}
            alt={display}
            fallback={initial}
            className="h-7 w-7"
          />
        </Link>
        <Link to={`/user/${f.username}`} className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-xs font-medium text-ink hover:underline">
            {display}
          </div>
          <div className="truncate text-[10px] text-muted-foreground">
            @{f.username}
          </div>
        </Link>
        <div className="shrink-0">{right}</div>
      </li>
    );
  };

  return (
    <div className="mt-3 border-t border-border pt-2">
      <p className="mb-2 text-xs font-medium text-ink">
        <span aria-hidden>👥</span> Users playing today
      </p>
      <ul className="space-y-2">
        {played.map((f) =>
          renderRow(
            f,
            f.solved ? (
              <span
                className="text-sm"
                aria-label="solved"
                title={`Solved in ${f.guess_count}`}
              >
                ✅
              </span>
            ) : (
              <span
                className="rounded-md bg-orange-50 px-2 py-0.5 text-[11px] font-medium text-orange-700"
                aria-label="played"
                title="Attempted today"
              >
                Played
              </span>
            ),
          ),
        )}
        {notPlayed.map((f) => {
          const already = sent.has(f.user_id);
          return renderRow(
            f,
            <button
              type="button"
              onClick={() => onNudge(f.user_id, f.username)}
              disabled={already || nudge.isPending}
              className="rounded-md border border-brand-200 bg-brand-25 px-2 py-0.5 text-[11px] font-medium text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {already ? 'Invited' : 'Invite'}
            </button>,
          );
        })}
      </ul>
    </div>
  );
}

/** Compact card variant — the default. Fits the dashboard right rail. */
export function YaariplayWidget() {
  const { data, isLoading, isError } = useYaariplayStats();
  const hoursLeft = hoursUntilResetIST();
  const players = data?.players_today ?? 0;
  const top = data?.top_player;

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Gamepad2 className="h-4 w-4 text-brand-700" /> Today&apos;s Yaariplay
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 p-4 pt-1 text-sm">
        <TilePreview />

        {isLoading ? (
          <>
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </>
        ) : isError ? (
          <p className="text-muted-foreground">Couldn&apos;t load right now.</p>
        ) : (
          <>
            <p className="text-ink">
              {players === 0
                ? "Be the first to crack today's word."
                : players === 1
                  ? (
                    <>
                      <span className="font-semibold">1 player</span> has cracked it — join them.
                    </>
                  )
                  : (
                    <>
                      Join{' '}
                      <span className="font-semibold">
                        {players.toLocaleString()}
                      </span>{' '}
                      players on today&apos;s puzzle.
                    </>
                  )}
            </p>

            {top ? (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Trophy className="h-3 w-3 shrink-0 text-amber-500" />
                <span>
                  <span className="font-medium text-ink">
                    @{top.username}
                  </span>{' '}
                  solved in {top.guess_count}{' '}
                  {top.guess_count === 1 ? 'guess' : 'guesses'}
                </span>
              </p>
            ) : null}

            <p className="text-xs text-muted-foreground">
              New puzzle in {hoursLeft}h · resets midnight IST
            </p>
          </>
        )}

        <Link
          to="/play/yaariplay"
          className="group mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-all hover:bg-brand-700 hover:shadow-sm"
        >
          Play now
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </Link>

        <WidgetFriendsLine />
      </CardContent>
    </Card>
  );
}

export default YaariplayWidget;
